/**
 * @file     PreApprovalModal.tsx
 * @purpose  Request an introduction: the broker sheet on every listing
 *           surface. Signed-out visitors give first name, last name, email and
 *           mobile phone; everyone gives income and monthly debts (the brokers
 *           need them; the home price comes from the listing). The broker
 *           handoff notice sits above the referral disclosure, which is quoted
 *           word for word. CSRF-gated POST to /api/pre-approval, which files
 *           the lead and the CRM note.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useFormMemory } from '@/hooks/useFormMemory';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { BROKER_REFERRAL_DISCLOSURE } from '@/core/content/brokerReferralDisclosure';
import { gateColors } from '@/components/gate/GateSheet';
import { contactPayload, validateContact, type ContactErrors, type ContactField, type ContactValues } from '@/services/leads/contactFields';
import { layoutTokens } from '@/design-system/tokens';
import { usePreApproval } from './PreApprovalContext';
import { ContactFields } from './ContactFields';
import { ListingSheet, SheetChip, SheetFooter, SheetLabel, SheetSent, SHEET_ICONS } from './ListingSheet';
import { gatedSecureFetch } from '@/services/phone-gate/gatedFetch';

type Phase = 'form' | 'submitting' | 'sent';

const EMPTY: ContactValues = { firstName: '', lastName: '', email: '', phone: '' };

const DEBTS = [
  { value: 'none', key: 'brokerSheet.debts.none', label: 'None' },
  { value: '<500', key: 'brokerSheet.debts.under500', label: 'Under $500' },
  { value: '500-1000', key: 'brokerSheet.debts.to1k', label: '$500 to $1k' },
  { value: '1000-2000', key: 'brokerSheet.debts.to2k', label: '$1k to $2k' },
  { value: '2000+', key: 'brokerSheet.debts.over2k', label: '$2k+' },
] as const;

function fmtNumber(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  return d ? Number(d).toLocaleString('en-CA') : '';
}

function parseNum(formatted: string): number | null {
  const n = Number(formatted.replace(/\D/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function PreApprovalModal() {
  const { isOpen, payload, close } = usePreApproval();
  const { isAuthenticated } = useAuth();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const { getMemory } = useFormMemory();
  const [contact, setContact] = useState<ContactValues>(EMPTY);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [income, setIncome] = useState('');
  const [incomeError, setIncomeError] = useState<string | null>(null);
  const [homePrice, setHomePrice] = useState('');
  const [debts, setDebts] = useState<string>('none');
  const [share, setShare] = useState(false);
  const consent = useConsentChoices();
  const [shareError, setShareError] = useState(false);
  const [phase, setPhase] = useState<Phase>('form');
  const [failed, setFailed] = useState(false);
  const openedRef = useRef<typeof payload>(null);

  useEffect(() => {
    if (!isOpen || !payload || openedRef.current === payload) return;
    openedRef.current = payload;
    const mem = getMemory();
    const [first = '', ...rest] = (mem.name ?? '').trim().split(/\s+/);
    setContact(isAuthenticated ? EMPTY : { firstName: first, lastName: rest.join(' '), email: mem.email ?? '', phone: mem.phone ?? '' });
    setErrors({});
    setIncome('');
    setIncomeError(null);
    setHomePrice(payload.listingPrice ? fmtNumber(String(Math.round(payload.listingPrice))) : '');
    setDebts('none');
    setShare(false);
    consent.reset();
    setShareError(false);
    setPhase('form');
    setFailed(false);
    // Once per opened listing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, payload]);

  if (!payload) return null;

  const pending = phase === 'submitting';
  const showFields = !isAuthenticated;
  const needsPrice = !payload.listingPrice;

  const submit = async () => {
    if (pending) return;
    const found = showFields ? validateContact(contact, t) : {};
    setErrors(found);
    const incomeOk = parseNum(income) !== null;
    setIncomeError(incomeOk ? null : t('brokerSheet.incomeError', { defaultValue: 'Enter your yearly income.' }));
    setShareError(!share);
    if (Object.keys(found).length > 0 || !incomeOk || !share || parseNum(homePrice) === null) return;
    setFailed(false);
    setPhase('submitting');
    try {
      const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
      const res = await gatedSecureFetch('/api/pre-approval', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          contact: showFields ? contactPayload(contact) : null,
          ...(showFields ? consent.payload(contact.phone) : {}),
          listing: { listingKey: payload.listingKey, listingTitle: payload.listingTitle, listingPrice: payload.listingPrice },
          financials: { annualIncome: parseNum(income), monthlyDebts: debts, homePrice: parseNum(homePrice) },
        }),
      });
      if (!res.ok) throw new Error(`pre-approval ${res.status}`);
      const data = (await res.json()) as { ok: boolean; kind: string; session?: { access_token: string; refresh_token: string } };
      if (data.session && data.kind === 'new_account') {
        const { createSupabaseBrowserClient } = await import('@/services/supabase/client');
        await createSupabaseBrowserClient().auth.setSession(data.session);
      }
      trackEvent({
        eventName: 'pre_approval_requested',
        eventType: 'form',
        category: 'conversion',
        label: 'listing_pre_approval',
        metadata: { listing_key: payload.listingKey, authenticated: isAuthenticated, kind: data.kind },
      });
      payload.onSubmitted?.();
      setPhase('sent');
    } catch {
      setFailed(true);
      setPhase('form');
    }
  };

  const title = phase === 'sent'
    ? t('listingSheet.sent', { defaultValue: 'Request sent' })
    : t('brokerSheet.title', { defaultValue: 'Request an introduction' });

  return (
    <ListingSheet
      open={isOpen}
      onClose={() => {
        if (!pending) close();
      }}
      listing={{ title: payload.listingTitle ?? '', price: payload.listingPrice }}
      title={title}
      titleHidden={phase === 'sent'}
      lead={phase === 'sent' ? undefined : t('preApprovalFlow.brokerIntro.lead', { defaultValue: 'Learn how to get a pre-approval from independent mortgage brokers.' })}
      stepKey={phase === 'sent' ? 'sent' : 'form'}
      testId="broker-sheet"
    >
      {phase === 'sent' ? (
        <SheetSent
          title={title}
          line={t('preApprovalFlow.brokerIntro.sent', { defaultValue: 'Ray is introducing you to one or more independent mortgage brokers. Each may contact you by email or phone.' })}
          primary={{ label: t('listingSheet.backToListing', { defaultValue: 'Back to listing' }), icon: SHEET_ICONS.back, onClick: close }}
        />
      ) : (
        <>
          {showFields ? (
            <ContactFields
              values={contact}
              errors={errors}
              onChange={(field: ContactField, value: string) => {
                setContact((prev) => ({ ...prev, [field]: value }));
                setErrors((prev) => ({ ...prev, [field]: undefined }));
              }}
              disabled={pending}
              idPrefix="pre-approval"
            />
          ) : null}
          <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: needsPrice ? { xs: '1fr', sm: '1fr 1fr' } : '1fr' }}>
            <TextField
              id="broker-income"
              label={t('brokerSheet.income', { defaultValue: 'Yearly income' })}
              value={income}
              onChange={(e) => {
                setIncome(fmtNumber(e.target.value));
                setIncomeError(null);
              }}
              error={Boolean(incomeError)}
              helperText={incomeError ?? undefined}
              required
              disabled={pending}
              slotProps={{ htmlInput: { inputMode: 'numeric' }, input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
            />
            {needsPrice ? (
              <TextField
                id="broker-home-price"
                label={t('brokerSheet.homePrice', { defaultValue: 'Home price' })}
                value={homePrice}
                onChange={(e) => setHomePrice(fmtNumber(e.target.value))}
                required
                disabled={pending}
                slotProps={{ htmlInput: { inputMode: 'numeric' }, input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
              />
            ) : null}
          </Box>
          <Box role="group" aria-labelledby="broker-debts-label" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SheetLabel id="broker-debts-label">{t('brokerSheet.debts', { defaultValue: 'Monthly debt payments' })}</SheetLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {DEBTS.map((option) => (
                <SheetChip key={option.value} pressed={debts === option.value} onToggle={() => setDebts(option.value)}>
                  {t(option.key, { defaultValue: option.label })}
                </SheetChip>
              ))}
            </Box>
          </Box>
          {/* Named before submit so a broker's first contact is a reply to this request (SOR/2013-221 s.3(b)). */}
          <Typography data-broker-handoff sx={{ fontSize: 14, lineHeight: 1.5, color: gateColors.textSecondary }}>
            {t('preApprovalFlow.broker.consent', {
              defaultValue:
                'Ray will introduce you to one or more independent mortgage brokers. Each may contact you by email or phone about the pre-approval you asked about. Ray Rasouli does not arrange mortgages.',
            })}
          </Typography>
          <Box
            data-referral-disclosure
            sx={{ px: 3.5, py: 3, borderRadius: `${layoutTokens.radius.sm}px`, backgroundColor: gateColors.surfaceSunken, fontSize: 12.5, lineHeight: 1.55, color: gateColors.textSecondary }}
          >
            {BROKER_REFERRAL_DISCLOSURE}{' '}
            <Box component="a" href="/disclosure" target="_blank" rel="noopener noreferrer" sx={{ color: gateColors.link }}>
              {t('listingDetail.costRail.disclosureLink', { defaultValue: 'Referral disclosure' })}
            </Box>
          </Box>
          <Box component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, cursor: 'pointer' }}>
            <Checkbox
              checked={share}
              onChange={(e) => {
                setShare(e.target.checked);
                setShareError(false);
              }}
              disabled={pending}
              sx={{ ml: -2, mt: -1.5, color: shareError ? gateColors.error : gateColors.borderStrong, '&.Mui-checked': { color: gateColors.primary } }}
            />
            <Typography component="span" sx={{ fontSize: 14, lineHeight: 1.45, color: shareError ? gateColors.error : gateColors.textSecondary }}>
              {t('preApprovalFlow.brokerIntro.share', { defaultValue: 'Share my details and income with the independent mortgage brokers Ray introduces.' })}
            </Typography>
          </Box>
          <SheetFooter
            cta={t('brokerSheet.submit', { defaultValue: 'Request an introduction' })}
            pending={pending}
            error={failed}
            onSubmit={() => void submit()}
            consent={showFields ? { action: t('forms.consent.requesting', { defaultValue: 'requesting' }) } : null}
            consentChoices={showFields ? { values: consent.values, onChange: consent.setValues, phone: true } : null}
          />
        </>
      )}
    </ListingSheet>
  );
}
